import { uiTestId } from '@7f-ui/test-ids'
import { type IconProps, WarningOctagon } from '@phosphor-icons/react'

import type { ComponentType, ElementType, PropsWithChildren, ReactNode } from 'react'

import { cx } from '../../cx'
import type { ButtonAsProps } from '../../polymorphic-props'
import { BaseButton } from '../button/button'
import { LoadingButton, type LoadingButtonProps } from '../button/loading-button'
import { Skeleton } from '../skeleton/skeleton'

function Root({ children }: PropsWithChildren) {
  return <div className="flex flex-col gap-6 rounded-xl px-5 py-8 shadow">{children}</div>
}

type TitleProps = PropsWithChildren<{
  isLoading?: boolean
}>

const Title = ({ children, isLoading }: TitleProps) => {
  if (isLoading) {
    return <Skeleton className="h-8" />
  }

  return (
    <div className="grid gap-2" data-testid={uiTestId('BookingBox.title')}>
      {children}
    </div>
  )
}

type FieldProps = PropsWithChildren<{ label: ReactNode }>

const Field = ({ children, label }: FieldProps) => (
  <div className="flex flex-col gap-2">
    <span className="font-heading5">{label}</span>
    {children}
  </div>
)

type ButtonComponentProps = PropsWithChildren<
  {
    Icon: ComponentType<IconProps>
    isError?: boolean
    message?: string
  } & ButtonAsProps<'button'>
>

const Button = ({ Icon, children, isError, message, ...rest }: ButtonComponentProps) => {
  return (
    <div className="flex flex-col justify-center gap-2">
      <BaseButton
        className={cx(
          'flex items-center gap-2 rounded-xl border border-primary/15 p-4 font-body-copy',
          isError && 'border-error'
        )}
        {...rest}
      >
        <Icon className="size-6 shrink-0" />
        <span className={cx('block min-w-0 grow truncate text-start', isError && 'text-error')}>
          {children}
        </span>
        {isError && (
          <WarningOctagon weight="bold" className="size-6 shrink-0 justify-self-end text-error" />
        )}
      </BaseButton>
      {message && (
        <span
          className={cx('pl-3 font-base-caption text-secondary-text', isError && 'text-error')}
          data-testid={uiTestId('BookingBox.message')}
        >
          {message}
        </span>
      )}
    </div>
  )
}

const CTA = <TAs extends ElementType>({
  children,
  ...rest
}: PropsWithChildren<LoadingButtonProps> & ButtonAsProps<TAs>) => (
  <LoadingButton {...rest}>{children}</LoadingButton>
)

export const BookingBox = Object.assign(Root, {
  Title,
  Field,
  Button,
  CTA,
})

export type UsePaxLabelParams = {
  adults?: number | null
  children?: number | null
  rooms?: number | null
  shorten?: boolean
  showRooms?: boolean
}

export function usePaxLabel({
  adults,
  children,
  rooms,
  shorten = true,
  showRooms = true,
}: UsePaxLabelParams) {
  const adultsLabel =
    adults &&
    (shorten ? `${adults} Erw.` : `${adults} × ${adults === 1 ? 'Erwachsener' : 'Erwachsene'}`)
  const childrenLabel = children && `${children} × ${children === 1 ? 'Kind' : 'Kinder'}`
  const roomsLabel = showRooms && `${rooms && rooms > 0 ? rooms : 1} × Zimmer`

  return [adultsLabel, childrenLabel, roomsLabel].filter(Boolean).join(', ')
}
